import React, { useState, useMemo } from "react";
import { useParams } from "react-router";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "./ui/table";
import { Printer } from "lucide-react";
import _ from "lodash";

import useFirebaseProject from "../hooks/useFirebaseProject";
import useFirebaseFirings from "../hooks/useFirebaseFirings";
import useFirebaseSegments from "../hooks/useFirebaseSegments";
import useFirebaseKiln from "../hooks/useFirebaseKiln";
import useFirebaseGlassData from "../hooks/useFirebaseGlassData";
import useFirebasePreferences from "../hooks/useFirebasePreferences";

import { convertLengthUnit, convertDegreesInSegment, degreeText } from "../helpers/unitHelpers";
import { glassImage } from "../helpers/logoHelpers";
import { correctlySizedPhoto } from "../helpers/photoHelpers";
import { formatDate } from "../helpers/dates";
import type { Firing, Segment, TemperatureUnit } from "../types";

const ProjectPrintPage = () => {
  const { id } = useParams();
  const [includeImages, setIncludeImages] = useState(true);
  const [includeMaterials, setIncludeMaterials] = useState(true);
  const [includeAdditional, setIncludeAdditional] = useState(false);

  const project = useFirebaseProject(id!);
  const kiln = useFirebaseKiln(project?.kiln!);
  const allFirings = useFirebaseFirings();
  const allSegments = useFirebaseSegments();
  const preferences = useFirebasePreferences();
  const glassData = useFirebaseGlassData(project?.glass!);

  const firings = useMemo(() => {
    if (!allFirings) return [];
    return Object.values(allFirings)
      .filter((f: any) => f?.project_id === id)
      .sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)) as Firing[];
  }, [allFirings, id]);

  const segmentsByFiring = useMemo(() => {
    if (!allSegments) return {} as Record<string, Segment[]>;
    const map: Record<string, Segment[]> = {};
    for (const firing of firings) {
      if (!firing.id) continue;
      map[firing.id] = Object.values(allSegments)
        .filter((s) => s.firing_id === firing.id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }
    return map;
  }, [allSegments, firings]);

  if (!project || !kiln) {
    return <div>Loading...</div>;
  }

  const degrees: TemperatureUnit = preferences?.degrees ?? "celsius";
  const lengthUnit = project.length_unit || "mm";
  const desiredUnit = preferences?.length_unit || "mm";
  const width = convertLengthUnit(lengthUnit, desiredUnit, project.width ?? 0);
  const depth = convertLengthUnit(lengthUnit, desiredUnit, project.depth ?? 0);
  const thickness = convertLengthUnit(lengthUnit, desiredUnit, project.thickness ?? 0);

  const materialsKeys = project.materials
    ? Object.keys(project.materials).sort(
        (a, b) => ((project.materials as any)[a].order ?? 0) - ((project.materials as any)[b].order ?? 0)
      )
    : [];

  const starsText = project.stars
    ? "\u2605".repeat(project.stars) + "\u2606".repeat(5 - project.stars)
    : "";

  return (
    <div>
      <div className="print:hidden mb-6 flex flex-wrap items-center gap-4 border-b pb-4">
        <h2 className="text-lg font-semibold mr-auto">Print Preview</h2>
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-images"
            checked={includeImages}
            onCheckedChange={(c) => setIncludeImages(c === true)}
          />
          <Label htmlFor="include-images">Include images</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-materials"
            checked={includeMaterials}
            onCheckedChange={(c) => setIncludeMaterials(c === true)}
          />
          <Label htmlFor="include-materials">Include materials</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="include-additional"
            checked={includeAdditional}
            onCheckedChange={(c) => setIncludeAdditional(c === true)}
          />
          <Label htmlFor="include-additional">Include additional fields</Label>
        </div>
        <Button onClick={() => window.print()}>
          <Printer className="mr-1 inline-block h-4 w-4" />
          Print
        </Button>
      </div>

      <div className="print:text-black">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-bold">{project.name}</h1>
          <img className="h-7 w-7 rounded-full" src={glassImage(project.glass)} alt={project.glass || ""} />
        </div>

        {project.date && (
          <p className="text-sm text-muted-foreground print:text-gray-600 mb-1">
            {formatDate(project.date)}
          </p>
        )}

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm mb-2">
          <span>
            Dimensions: {width} x {depth} x {thickness} ({desiredUnit})
          </span>
          {kiln.name && <span>Kiln: {kiln.name}</span>}
          {starsText && <span>Rating: {starsText}</span>}
        </div>

        {project.tags && project.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {project.tags.map((tag, i) => (
              <Badge key={i} variant="secondary">{tag}</Badge>
            ))}
          </div>
        )}

        {project.sale_status && (
          <div className="mb-2">
            <Badge variant="outline">{project.sale_status}</Badge>
          </div>
        )}

        {project.notes && (
          <div className="mb-3">
            <h3 className="font-semibold">Notes</h3>
            <p className="whitespace-pre-wrap text-sm">{project.notes}</p>
          </div>
        )}

        {includeAdditional &&
          (project.material_cost || project.firing_cost || project.labour_cost || project.sale_price || project.sold_to) && (
            <div className="mb-3">
              <h3 className="font-semibold mb-1">Financial Details</h3>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm max-w-md">
                {project.material_cost && (
                  <>
                    <span className="text-muted-foreground">Material Cost</span>
                    <span>{project.material_cost}</span>
                  </>
                )}
                {project.firing_cost && (
                  <>
                    <span className="text-muted-foreground">Firing Cost</span>
                    <span>{project.firing_cost}</span>
                  </>
                )}
                {project.labour_cost && (
                  <>
                    <span className="text-muted-foreground">Labour Cost</span>
                    <span>{project.labour_cost}</span>
                  </>
                )}
                {project.sale_price && (
                  <>
                    <span className="text-muted-foreground">Sale Price</span>
                    <span>{project.sale_price}</span>
                  </>
                )}
                {project.sold_to && (
                  <>
                    <span className="text-muted-foreground">Sold To</span>
                    <span>{project.sold_to}</span>
                  </>
                )}
              </div>
            </div>
          )}

        {includeMaterials && materialsKeys.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold mb-1">Materials</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Glass</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {materialsKeys.map((key) => {
                  const mat = (project.materials as any)[key];
                  if (!mat) return null;
                  const inv = glassData?.inventory?.[mat.glass_reference];
                  return (
                    <TableRow key={key}>
                      <TableCell>{mat.glass_reference}</TableCell>
                      <TableCell>{inv?.description || mat.description}</TableCell>
                      <TableCell>{inv?.type}</TableCell>
                      <TableCell>{mat.glass || project.glass}</TableCell>
                      <TableCell>{mat.notes}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {firings.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold mb-1">Firings</h3>
            {firings.map((firing, index) => {
              const segments = firing.id ? segmentsByFiring[firing.id] ?? [] : [];
              return (
                <div key={firing.id} className="mb-3">
                  <h4 className="font-medium">
                    {index + 1}. {firing.name}
                    {firing.date && (
                      <span className="text-sm text-muted-foreground ml-2">
                        {formatDate(firing.date)}
                      </span>
                    )}
                  </h4>
                  {firing.notes && <p className="text-sm mb-1">{firing.notes}</p>}
                  {segments.length > 0 && (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>#</TableHead>
                          <TableHead>Rate ({degreeText(degrees)}/hr)</TableHead>
                          <TableHead>Target ({degreeText(degrees)})</TableHead>
                          <TableHead>Hold (min)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {segments.map((seg, si) => {
                          const converted = convertDegreesInSegment(seg, degrees);
                          return (
                            <TableRow key={seg.id || si}>
                              <TableCell>{si + 1}</TableCell>
                              <TableCell>{converted.rate}</TableCell>
                              <TableCell>{converted.temperature}</TableCell>
                              <TableCell>{seg.hold}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {includeImages && project.photos && project.photos.length > 0 && (
          <div className="mb-4">
            <h3 className="font-semibold mb-1">Photos</h3>
            <div className={project.photos.length > 2 ? "grid grid-cols-3 gap-2" : "flex gap-2"}>
              {project.photos.map((photo: any, i: number) => {
                const src = correctlySizedPhoto(photo, "medium")?.uri;
                if (!src) return null;
                return (
                  <div key={i} className="text-center">
                    <img src={src} alt="" className="max-h-64 object-contain mx-auto" />
                    {photo.type && (
                      <p className="text-xs text-muted-foreground mt-1 capitalize">{photo.type}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPrintPage;
